import { FC, useCallback, useEffect, useRef, useState } from "react";
import { IFurniture } from "../../../interfaces/IFurniture";
import furnitureController from "../../../Api/furnitureController";
import { useToast } from "../../../hooks/useToast";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardMedia,
    CardContent,
    Paper,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Button,
    CircularProgress
} from "@mui/material";
import { useUser } from "../../../hooks/useUser";
import { baseURL } from "../../../config";

const BASE_URL = baseURL;

interface ModuleEditorProps { }

const typeBgColor = (type?: string) => {
    switch (type) {
        case "left": return "#1976d2";
        case "right": return "#388e3c";
        case "middle": return "#fbc02d";
        case "corner": return "#d32f2f";
        case "solo": return "#7b1fa2";
        default: return "#fff";
    }
};
const typeTextColor = (type?: string) => {
    switch (type) {
        case "middle": return "#222";
        case "left":
        case "right":
        case "corner":
        case "solo":
            return "#fff";
        default: return "#b71c1c";
    }
};

const ModuleEditor: FC<ModuleEditorProps> = () => {
    const [actualModule, setActualModule] = useState<string | null>(null);
    const [furniture, setFurniture] = useState<IFurniture[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const toast = useRef(useToast()).current;
    const user = useUser()

    // Refy do scrollowania modułów
    const moduleRefs = useRef<{ [key: string]: HTMLLIElement | null }>({});

    const getFurniture = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        const res = await furnitureController.getFurnitureWithModulesWithoutTypes();
        if (!res.ok || !res.furniture) {
            setLoadError(res.message || 'Nie udało się pobrać modułów.');
            setLoading(false);
            return toast.error(res.message || 'Nie udało się pobrać modułów.');
        }
        setFurniture(res.furniture);
        setActualModule(res.furniture[0]?.modules?.length ? res.furniture[0].modules[0]._id : null);
        setLoading(false);
    }, [toast]);

    // Funkcja do przechodzenia do kolejnego modułu i scrollowania
    const setNextModule = () => {
        // Znajdź indeks mebla i modułu
        let furnitureIdx = -1;
        let moduleIdx = -1;
        for (let i = 0; i < furniture.length; i++) {
            const mods = furniture[i]?.modules ?? [];
            const idx = mods.findIndex(m => m?._id === actualModule);
            if (idx !== -1) {
                furnitureIdx = i;
                moduleIdx = idx;
                break;
            }
        }
        // Jeśli nie znaleziono, ustaw pierwszy moduł pierwszego mebla
        if (
            furnitureIdx === -1 ||
            moduleIdx === -1 ||
            !furniture[0]?.modules?.[0]?._id
        ) {
            if (furniture[0]?.modules?.[0]?._id) {
                setActualModule(furniture[0].modules[0]._id);
                setTimeout(() => {
                    const id = furniture[0]?.modules?.[0]?._id;
                    if (id && moduleRefs.current[id]) {
                        moduleRefs.current[id]?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                            inline: "center",
                        });
                    }
                }, 0);
            }
            return;
        }
        const currentFurniture = furniture[furnitureIdx];
        const currentModules = currentFurniture?.modules ?? [];
        // Jeśli jest kolejny moduł w tym samym meblu
        if (moduleIdx + 1 < currentModules.length) {
            const nextId = currentModules[moduleIdx + 1]?._id ?? undefined;
            setActualModule(nextId ?? null);
            setTimeout(() => {
                if (nextId && moduleRefs.current[nextId]) {
                    moduleRefs.current[nextId]?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                        inline: "center",
                    });
                }
            }, 0);
            return;
        }
        // Jeśli jest kolejny mebel z modułami
        for (let i = furnitureIdx + 1; i < furniture.length; i++) {
            const modules = furniture[i]?.modules;
            if (Array.isArray(modules) && modules.length > 0 && modules[0]?._id) {
                const nextId = modules[0]._id;
                setActualModule(nextId);
                setTimeout(() => {
                    if (nextId && moduleRefs.current[nextId]) {
                        moduleRefs.current[nextId]?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                            inline: "center",
                        });
                    }
                }, 0);
                return;
            }
        }
        // Jeśli nie ma już kolejnych, wróć na pierwszy moduł pierwszego mebla
        if (furniture[0]?.modules?.[0]?._id) {
            const nextId = furniture[0].modules[0]._id;
            setActualModule(nextId);
            setTimeout(() => {
                if (nextId && moduleRefs.current[nextId]) {
                    moduleRefs.current[nextId]?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                        inline: "center",
                    });
                }
            }, 0);
        }
    };

    const setPreviousModule = () => {
        // Znajdź indeks mebla i modułu
        let furnitureIdx = -1;
        let moduleIdx = -1;
        for (let i = 0; i < furniture.length; i++) {
            const mods = furniture[i]?.modules ?? [];
            const idx = mods.findIndex(m => m?._id === actualModule);
            if (idx !== -1) {
                furnitureIdx = i;
                moduleIdx = idx;
                break;
            }
        }
        // Jeśli nie znaleziono, ustaw ostatni moduł ostatniego mebla
        if (
            furnitureIdx === -1 ||
            moduleIdx === -1
        ) {
            for (let i = furniture.length - 1; i >= 0; i--) {
                const mods = furniture[i]?.modules ?? [];
                if (mods.length > 0 && mods[mods.length - 1]?._id) {
                    const prevId = mods[mods.length - 1]._id;
                    setActualModule(prevId);
                    setTimeout(() => {
                        if (prevId && moduleRefs.current[prevId]) {
                            moduleRefs.current[prevId]?.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                                inline: "center",
                            });
                        }
                    }, 0);
                    return;
                }
            }
            return;
        }
        const currentFurniture = furniture[furnitureIdx];
        const currentModules = currentFurniture?.modules ?? [];
        // Jeśli jest poprzedni moduł w tym samym meblu
        if (moduleIdx - 1 >= 0) {
            const prevId = currentModules[moduleIdx - 1]?._id ?? undefined;
            setActualModule(prevId ?? null);
            setTimeout(() => {
                if (prevId && moduleRefs.current[prevId]) {
                    moduleRefs.current[prevId]?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                        inline: "center",
                    });
                }
            }, 0);
            return;
        }
        // Jeśli jest poprzedni mebel z modułami
        for (let i = furnitureIdx - 1; i >= 0; i--) {
            const mods = furniture[i]?.modules ?? [];
            if (mods.length > 0 && mods[mods.length - 1]?._id) {
                const prevId = mods[mods.length - 1]._id;
                setActualModule(prevId);
                setTimeout(() => {
                    if (prevId && moduleRefs.current[prevId]) {
                        moduleRefs.current[prevId]?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                            inline: "center",
                        });
                    }
                }, 0);
                return;
            }
        }
        // Jeśli nie ma już poprzednich, wróć na ostatni moduł ostatniego mebla
        for (let i = furniture.length - 1; i >= 0; i--) {
            const mods = furniture[i]?.modules ?? [];
            if (mods.length > 0 && mods[mods.length - 1]?._id) {
                const prevId = mods[mods.length - 1]._id;
                setActualModule(prevId);
                setTimeout(() => {
                    if (prevId && moduleRefs.current[prevId]) {
                        moduleRefs.current[prevId]?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                            inline: "center",
                        });
                    }
                }, 0);
                return;
            }
        }
    };

    const handleClickKey = async (e: React.KeyboardEvent<HTMLDivElement>) => {
        let type: string | null = null;
        if (e.key === 'ArrowUp') return setPreviousModule();
        if (e.key === 'ArrowDown') return setNextModule();
        if (e.key === '4') type = 'left';
        if (e.key === '5') type = 'middle';
        if (e.key === '6') type = 'right';
        if (e.key === '7') type = 'corner';
        if (e.key === '0') type = 'solo';

        if (type && actualModule) {
            // Znajdź id mebla dla aktualnego modułu
            const furnitureId = furniture.find(f => f.modules?.some(m => m._id === actualModule))?._id;
            if (!furnitureId) {
                toast.error("Nie znaleziono mebla dla tego modułu");
                return;
            }

            const res = await furnitureController.editModuleType({
                furnitureId,
                moduleId: actualModule,
                moduleType: type as any,
            });

            if (res.ok) {
                toast.success("Zmieniono typ modułu");
                // Aktualizuj typ modułu w stanie furniture
                setFurniture(prev =>
                    prev.map(f =>
                        f._id === furnitureId
                            ? {
                                ...f,
                                modules: f.modules?.map(m =>
                                    m._id === actualModule
                                        ? { ...m, moduleType: type }
                                        : m
                                )
                            }
                            : f
                    )
                );
                setNextModule();
            } else {
                toast.error(res.message);
            }
        }
    };


    useEffect(() => {
        getFurniture();
    }, [getFurniture]);


    if (!user.isAuth) return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
            <Paper sx={{ p: 4, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Typography variant="h5" sx={{ mb: 3 }}>
                    BRAK DOSTĘPU
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => window.location.href = "/logowanie"}
                >
                    Zaloguj się
                </Button>
            </Paper>
        </Box>
    )

    if (loading) return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 280 }}>
            <CircularProgress />
        </Box>
    );

    if (loadError) return (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 3, bgcolor: "#fff8f8" }}>
            <Typography variant="h6" fontWeight={800} color="error.main">Nie udało się wczytać modułów</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>{loadError}</Typography>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={getFurniture}>Spróbuj ponownie</Button>
        </Paper>
    );

    if (furniture.length === 0) return (
        <Paper variant="outlined" sx={{ p: 5, textAlign: "center", borderRadius: 3, bgcolor: "#f8fafc" }}>
            <Typography variant="h5" fontWeight={800} color="success.main">Wszystkie typy są uzupełnione</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
                Edytor pokazuje wyłącznie moduły bez przypisanego typu. Obecnie nie ma takich modułów.
            </Typography>
        </Paper>
    );

    return (
        <Box
            sx={{ p: 3 }}
            tabIndex={0}
            onKeyDown={handleClickKey}
        >
            {furniture.map((f, index) => (
                <Paper key={index} elevation={3} sx={{ mb: 4, p: 2 }}>
                    <Grid container spacing={3}>
                        {/* Left: Image and name */}
                        <Grid item xs={12} md={3} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Card sx={{ width: "100%", maxWidth: 220, mb: 2 }}>
                                <CardMedia
                                    component="img"
                                    height="180"
                                    image={BASE_URL + f.images[0]}
                                    alt={f.name}
                                    sx={{ objectFit: "contain", background: "#fafafa" }}
                                />
                                <CardContent sx={{ p: 1 }}>
                                    <Typography variant="h6" align="center" fontWeight={700}>
                                        {f.name}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        {/* Right: Modules list */}
                        <Grid item xs={12} md={9}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                                Moduły:
                            </Typography>
                            <List>
                                {(f.modules || []).map((mod, modIdx) => {
                                    // Kolor tła i tekstu zależny od typu modułu (nie tylko przy zaznaczeniu)
                                    const bg = typeBgColor(mod.moduleType ?? undefined);
                                    const color = typeTextColor(mod.moduleType ?? undefined);
                                    return (
                                        <ListItem
                                            key={mod._id || modIdx}
                                            ref={el => {
                                                if (mod._id) moduleRefs.current[mod._id] = el;
                                            }}
                                            alignItems="flex-start"
                                            sx={{
                                                mb: 2,
                                                borderRadius: 2,
                                                boxShadow: 1,
                                                bgcolor: bg,
                                                cursor: "pointer",
                                                transition: "background 0.2s",
                                                border: actualModule === mod._id ? "2px solid #222" : undefined,
                                            }}
                                            onClick={() => setActualModule(mod._id)}
                                        >
                                            <ListItemAvatar sx={{ minWidth: 0, mr: 2 }}>
                                                <img
                                                    src={BASE_URL + mod.image}
                                                    alt={mod.name}
                                                    style={{
                                                        width: 164,
                                                        height: 164,
                                                        objectFit: "contain",
                                                        background: "#fff",
                                                        borderRadius: 8,
                                                        display: "block",
                                                    }}
                                                />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                        {mod.name}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            {mod.description}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: "#555" }}>
                                                            <strong>Wymiary:</strong>{" "}
                                                            {mod.width ? `Szerokość: ${mod.width} cm` : ""}
                                                            {mod.depth ? `, Głębokość: ${mod.depth} cm` : ""}
                                                            {mod.height ? `, Wysokość: ${mod.height} cm` : ""}
                                                        </Typography>
                                                        <Typography
                                                            variant="h2"
                                                            sx={{
                                                                color,
                                                                mt: 2,
                                                                fontWeight: 900,
                                                                letterSpacing: 2,
                                                                textShadow: actualModule === mod._id ? "1px 1px 8px rgba(0,0,0,0.18)" : "none",
                                                                textAlign: "left",
                                                                fontSize: { xs: "2.5rem", md: "3.5rem" },
                                                                lineHeight: 1.1,
                                                            }}
                                                        >
                                                            {mod.moduleType
                                                                ? mod.moduleType.toUpperCase()
                                                                : <span style={{ color: "#b71c1c", fontSize: "1.5rem" }}>BRAK TYPU</span>
                                                            }
                                                        </Typography>
                                                    </>
                                                }
                                            />
                                        </ListItem>
                                    );
                                })}
                                {(f.modules?.length === 0 || !f.modules) && (
                                    <ListItem>
                                        <ListItemText primary={<Typography color="text.secondary">Brak modułów do wyświetlenia.</Typography>} />
                                    </ListItem>
                                )}
                            </List>
                        </Grid>
                    </Grid>
                </Paper>
            ))}
        </Box>
    );
};

export default ModuleEditor;
